import { PortfolioNav, ThemeSwitcher, buildPortfolioProducts, type PortfolioNavLink } from '@saas-pages/shared/public/app-shell'
import {
  resolveUtilitySourceToolKey,
  type UtilityAnalyticsAttributionRoute,
} from '@saas-pages/shared/public/registry'
import { Seo } from '@saas-pages/shared/public/seo'
import { BarcodeGeneratorPage } from '@/pages/barcode-generator/BarcodeGeneratorPage'
import { BlogIndexPage } from '@/pages/blog/BlogIndexPage'
import { BlogPostPage } from '@/pages/blog/BlogPostPage'
import { QrGeneratorPage } from '@/pages/qr-generator/QrGeneratorPage'
import { BarcodeScannerPage, QrScannerPage } from '@/pages/qr-scanner/QrScannerPage'
import { ToolsHomePage } from '@/pages/tools-home/ToolsHomePage'
import { seoFor, seoRoutes, type RouteKey } from '@/seo/routes'
import { useLocale, useT } from '@/shared/i18n'
import { LangSwitcher } from '@/widgets/lang-switcher/LangSwitcher'

interface AppProps {
  pathname?: string
}

export function App({ pathname }: AppProps = {}) {
  const t = useT()
  const { locale } = useLocale()
  const current = currentRoute(pathname)
  const routeKey = current.key
  const isBlogPost = routeKey === 'blog' && Boolean(current.slug)
  const seo = seoFor(routeKey, locale)
  const page =
    routeKey === 'blog' && current.slug ? (
      <BlogPostPage slug={current.slug} />
    ) : routeKey === 'blog' ? (
      <BlogIndexPage />
    ) : routeKey === 'barcode-generator' ? (
      <BarcodeGeneratorPage />
    ) : routeKey === 'barcode-scanner' ? (
      <BarcodeScannerPage />
    ) : routeKey === 'qr-scanner' ? (
      <QrScannerPage />
    ) : routeKey === 'qr-generator' ? (
      <QrGeneratorPage />
    ) : (
      <ToolsHomePage />
    )
  const navItems: PortfolioNavLink[] = [
    {
      key: 'barcode-scanner',
      label: t.nav.barcodeScanner,
      href: '/skaner-shtrih-koda',
      current: routeKey === 'barcode-scanner',
    },
    {
      key: 'tools',
      label: t.nav.tools,
      href: '/',
      current: routeKey === 'index',
    },
    {
      key: 'qr-generator',
      label: t.nav.qrGenerator,
      href: '/qr-kod',
      current: routeKey === 'qr-generator',
    },
    {
      key: 'barcode-generator',
      label: t.nav.barcodeGenerator,
      href: '/generator-shtrih-kodov',
      current: routeKey === 'barcode-generator',
    },
    {
      key: 'qr-scanner',
      label: t.nav.qrScanner,
      href: '/skaner-qr-koda',
      current: routeKey === 'qr-scanner',
    },
    {
      key: 'blog',
      label: t.nav.blog,
      href: '/blog',
      current: routeKey === 'blog',
    },
  ]

  return (
    <div className="min-h-screen bg-background text-foreground">
      {!isBlogPost && <Seo metadata={seo.metadata} />}

      <PortfolioNav
        brandLabel="qrka.ru"
        brandHref="/"
        products={buildPortfolioProducts('qrka')}
        items={navItems}
        sourceToolKey={resolveUtilitySourceToolKey(QR_TOOL_ATTRIBUTION, seoRoutes.routes[routeKey].path)}
        actions={(
          <>
            <ThemeSwitcher storageKey="qrka.theme" />
            <LangSwitcher />
          </>
        )}
      />

      <main className="px-4 py-4 sm:py-8">
        {page}
      </main>
    </div>
  )
}

const QR_TOOL_ROUTE_KEYS = [
  'qr-generator',
  'barcode-generator',
  'barcode-scanner',
  'qr-scanner',
] as const satisfies readonly RouteKey[]

const QR_TOOL_ATTRIBUTION = QR_TOOL_ROUTE_KEYS.map((key) => ({
  path: seoRoutes.routes[key].path,
  analyticsKey: key,
})) satisfies readonly UtilityAnalyticsAttributionRoute[]

interface CurrentRoute {
  key: RouteKey
  slug?: string
}

function currentRoute(staticPathname?: string): CurrentRoute {
  if (!staticPathname && typeof window === 'undefined') {
    return { key: 'index' }
  }

  const pathname = (staticPathname ?? window.location.pathname).replace(/\/+$/, '') || '/'
  if (pathname === '/qr-kod') return { key: 'qr-generator' }
  if (pathname === '/generator-shtrih-kodov') return { key: 'barcode-generator' }
  if (pathname === '/skaner-shtrih-koda') return { key: 'barcode-scanner' }
  if (pathname === '/skaner-qr-koda') return { key: 'qr-scanner' }
  if (pathname === '/blog') return { key: 'blog' }
  if (pathname.startsWith('/blog/')) {
    return { key: 'blog', slug: safeDecodeSlug(pathname.slice('/blog/'.length)) }
  }
  return { key: 'index' }
}

function safeDecodeSlug(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}
